import type { LanguageOption } from "./policyApiService";
import { policyApiService } from "./policyApiService";

// Service for managing languages cache
export class LanguagesCacheService {
  private static readonly CACHE_KEY = "policy_languages_cache";
  private static readonly CACHE_EXPIRY_KEY = "policy_languages_cache_expiry";
  private static readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  /**
   * Get cached languages if available and not expired
   */
  static getCachedLanguages(): LanguageOption[] | null {
    try {
      const cachedData = sessionStorage.getItem(this.CACHE_KEY);
      const cacheExpiry = sessionStorage.getItem(this.CACHE_EXPIRY_KEY);

      if (cachedData && cacheExpiry && Date.now() < parseInt(cacheExpiry)) {
        return JSON.parse(cachedData);
      }

      return null; // Cache miss or expired
    } catch (error) {
      console.error("Error reading languages cache:", error);
      return null;
    }
  }

  /**
   * Cache languages data
   */
  static setCachedLanguages(languages: LanguageOption[]): void {
    try {
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(languages));
      sessionStorage.setItem(
        this.CACHE_EXPIRY_KEY,
        (Date.now() + this.CACHE_DURATION).toString()
      );
    } catch (error) {
      console.error("Error caching languages:", error);
    }
  }

  /**
   * Clear languages cache
   */
  static clearCache(): void {
    try {
      sessionStorage.removeItem(this.CACHE_KEY);
      sessionStorage.removeItem(this.CACHE_EXPIRY_KEY);
    } catch (error) {
      console.error("Error clearing languages cache:", error);
    }
  }

  /**
   * Check if cache exists and is valid
   */
  static isCacheValid(): boolean {
    try {
      const cachedData = sessionStorage.getItem(this.CACHE_KEY);
      const cacheExpiry = sessionStorage.getItem(this.CACHE_EXPIRY_KEY);

      return !!(
        cachedData &&
        cacheExpiry &&
        Date.now() < parseInt(cacheExpiry)
      );
    } catch (error) {
      console.error("Error checking cache validity:", error);
      return false;
    }
  }

  /**
   * Get cache expiry time
   */
  static getCacheExpiry(): Date | null {
    try {
      const cacheExpiry = sessionStorage.getItem(this.CACHE_EXPIRY_KEY);
      return cacheExpiry ? new Date(parseInt(cacheExpiry)) : null;
    } catch (error) {
      console.error("Error getting cache expiry:", error);
      return null;
    }
  }

  /**
   * Force refresh languages cache across the application
   * This can be called when languages are known to have changed
   */
  static async refreshLanguagesCache(): Promise<LanguageOption[]> {
    this.clearCache();

    try {
      const response = await policyApiService.getAvailableLanguages();
      this.setCachedLanguages(response.data);
      return response.data;
    } catch (error) {
      console.error("Error refreshing languages cache:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const languagesCacheService = LanguagesCacheService;
